<template>
  <div class="container main-container">
    <h1 class="h1 mb-4">{{ 'Terms of Use' | translate }}</h1>

    <div>
      <h2 class="h2">{{ 'Please Read These Terms of Use Carefully' | translate }}</h2>
      <p class="p">
        {{
          `By using this Zinpro FirstStep website, its underlying systems and any related mobile
        applications (the ‘Website’), you agree to be bound by the following terms and conditions
        (the 'Terms of Use'). This Website is owned and operated by Zinpro Corporation (‘Zinpro’).
        As used in this Terms of Use, 'Zinpro,' 'we,' 'us' and 'our' all refer to the owner of this
        Website, including any of the companies that it controls, such as its subsidiaries and
        affiliates.` | translate
        }}
      </p>
      <p class="p">
        {{
          `We may change the Terms of Use at any time without notice to you. Please review the Terms of
        Use each time you visit the Website. By using this Website, it means you accept and agree to
        be bound by the most recent version of the Terms of Use. If you do not agree to be bound by
        the Terms of Use, you may not view, access or use this Website.` | translate
        }}
      </p>
      <h2 class="h2">{{ 'Disclaimer of Limited Use' | translate }}</h2>
      <p class="p">
        {{
          `The Website is intended to be used only as a supplemental educational support tool to assist
        commercial dairy herd personnel to assess dairy cattle lameness. Recommendations on lameness
        assessment, lameness prediction and the impact of lameness on profit potential are based on
        current recommendations available when this service tool was written. Guidelines on lameness
        management and prediction on impact on profits are subject to change as new data becomes
        available. This Website is only available to current customers of Zinpro who are purchasing
        our products.` | translate
        }}
      </p>
      <p class="p">
        {{ 'By registering for this Website, you agree to the following:' | translate }}
      </p>
      <ul class="ul">
        <li>
          {{
            'The Website is only licensed for your use while supporting and using Zinpro Performance Minerals®.'
              | translate
          }}
        </li>
        <li>
          {{
            "Only registered users meeting Zinpro's acceptance criteria are allowed to view or use this Website."
              | translate
          }}
        </li>
        <li>
          {{
            'Independent consultants, such as researchers affiliated with universities, are utilized by the Website to review and assess lameness data.'
              | translate
          }}
        </li>
      </ul>
      <ul class="ul">
        <li>
          {{
            'By using this Website, you agree to the disclosure and sharing of lameness assessment'
              | translate
          }}
          {{ 'information associated with your heard to independent consultants.' | translate }}
        </li>
        <li>
          {{
            'Zinpro reserves the right to terminate your use of the Website at any time.'
              | translate
          }}
        </li>
      </ul>
      <h2 class="h2">{{ 'License Grant' | translate }}</h2>
      <p class="p">
        {{
          `Zinpro grants you a limited, nonexclusive, nontransferable license to access and use the
        Website and its contents for informational and personal purposes. You are not permitted to
        use the Website for any purposes or in any manner other than as intended. You agree to use
        the Website and its contents only for proper, lawful purposes and not for any illegal,
        unethical, fraudulent, deceptive or other unauthorized purposes. You agree not to use any
        device, software, or routine to interfere with the proper working of the Website. You agree
        not to attempt to collect information about others, including email or personal data.`
            | translate
        }}
      </p>
      <h2 class="h2">{{ 'Your Feedback, Comments and Ideas' | translate }}</h2>
      <p class="p">
        {{
          `While we are pleased to receive feedback, comments and ideas from visitors to this Website,
        we want you to understand that any information that you submit through this Website will be
        considered non-personal, non-confidential and non-proprietary. If you transmit any ideas,
        information, concepts, know-how or techniques or materials to us through this Website, you
        hereby grant Zinpro an unrestricted, royalty-free, irrevocable license to use, reproduce,
        display, perform, modify, transmit and distribute them in any medium, and agree that Zinpro
        is free to use them for any purpose. You agree that we shall not be under any obligation
        whatsoever to return any unsolicited submissions from you. You agree that we shall be
        entitled to the unrestricted use of the unsolicited submissions for any purpose whatsoever,
        commercial or otherwise, without compensation to you or any other provider of the
        unsolicited submissions.` | translate
        }}
      </p>
      <h2 class="h2">{{ 'Links' | translate }}</h2>
      <p class="p">
        {{
          `The firststep.zinpro.com website includes links to other Web pages. These links to websites
        are operated by other proprietary organizations, government agencies and private
        businesses/parties. When you link to another site, you are no longer on the
        firststep.zinpro.com Website, and we are not responsible for any of the content you may see,
        use, or have access to. When you link to another website, you are subject to the terms and
        conditions of that website. The inclusion of any links does not necessarily imply a
        recommendation or endorse the views expressed within them.` | translate
        }}
      </p>
      <h2 class="h2">{{ 'Accuracy of Content' | translate }}</h2>
      <p class="p">
        {{
          `Neither Zinpro nor any officer or employee of Zinpro (the ‘Zinpro Parties’), warrants the
        accuracy, reliability or timeliness of any information published by this Website. Neither do
        the Zinpro Parties endorse any content, viewpoints, products or services linked from this
        Website. The Zinpro Parties shall not be held liable for any losses caused by reliance on
        the accuracy, reliability or timeliness of such information. Portions of such information
        may be incorrect or not current. Any person or entity that relies on any information obtained
        from this Website does so at his or her own risk.` | translate
        }}
      </p>
      <p class="p">
        {{
          `The firststep.zinpro.com Website is presented as an informational service. Any content should be independently verified. State and federal agency-authored documents are in the public domain. Copyright and access restrictions apply.`
            | translate
        }}
      </p>
      <h2 class="h2">{{ 'Information We Collect' | translate }}</h2>
      <p class="p">
        {{
          `When you view the Website, you do so as a registered user. We do not collect any personal
        information that identifies you, unless you specifically provide us with that contact
        information voluntarily. Personal information includes information that is unique to an
        individual, such as a name, address, e-mail address or telephone number. The only personal
        information that Zinpro obtains through its Website is supplied voluntarily by Website
        visitors. We do not share information from registered users, such as the location of your
        farm, with other registered users. By submitting personal information, you grant Zinpro the
        right to transmit, monitor, retrieve, store and use your information in connection with the
        operation of the Website. You might be asked to provide personal information, such as demographic 
        information, if you choose to complete any of the online forms used for event registration, 
        special events and promotions, appointment requests, and to provide feedback. 
        The information required to complete the transaction is noted on each specific form. 
        Additional questions might be included to help us improve the services we offer. 
        Please refer to Zinpro’s Privacy Policy for additional information regarding the information we collect.`
            | translate
        }}
      </p>
      <h2 class="h2">{{ 'How We Use This Information' | translate }}</h2>
      <p class="p">
        {{
          `We may use the information you provide to fulfill online requests and respond to customer
        service inquiries or in other ways as required by law. Requested information on voluntary
        forms is used to provide the services that you request, such as event registration, special
        events and promotions, appointment requests, and to provide feedback. Only authorized personnel have access to your information, including those who need the information provided to perform a specific task.`
            | translate
        }}
      </p>
      <p class="p">
        {{
          `The Website may use 'cookies' to determine the number of times a page on the Website has
        been accessed. Cookies are small computer files that a website can transfer to your
        computer. Examples of information we may collect include how often someone visits our
        Website and their activities while on our Website. We use this information in the aggregate to understand how our visitors as a group use different resources. These reports do not contain any personally identifiable information. Most Internet browsers automatically accept cookies, but you can set your browser to refuse them or to alert you when they are being sent.`
            | translate
        }}
      </p>
      <p class="p">
        {{
          `Zinpro occasionally will engage reputable third-party vendors to help us manage our Website,
        provide content and information and enable us to interact better with our visitors.`
            | translate
        }}
      </p>
      <h2 class="h2">{{ 'Disclaimers' | translate }}</h2>
      <p class="font-weight-bold text-uppercase">
        {{
          `ZINPRO MAKES NO REPRESENTATIONS OR WARRANTIES THAT USE OF THIS WEBSITE WILL BE
        UNINTERRUPTED, RELIABLE, SUITABLE, ERROR-FREE, FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS
        OR OTHERWISE. YOU ARE RESPONSIBLE FOR TAKING ALL PRECAUTIONS NECESSARY TO ENSURE THAT ANY
        CONTENT YOU MAY OBTAIN FROM THIS WEBSITE IS FREE OF VIRUSES AND ANY OTHER POTENTIALLY
        DESTRUCTIVE COMPUTER CODE.` | translate
        }}
      </p>
      <p class="p">
        {{
          `The information contained in this Website is for general information purposes only. The
        information is provided by Zinpro and while we endeavor to keep the information up to date
        and correct, we make no representations or warranties of any kind, express or implied, about
        the completeness, accuracy, reliability, suitability or availability with respect to the
        Website or the information, products, services, or related graphics contained on the Website
        for any purpose. Any reliance you place on such information is therefore strictly at your
        own risk.` | translate
        }}
      </p>
      <p class="p">
        {{
          `In no event will Zinpro be liable for any loss or damage including without limitation,
        indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss
        of data or profits arising out of, or in connection with, the use of this Website, to the
        extent permitted by applicable law.` | translate
        }}
      </p>
      <p class="p">
        {{
          `Every effort is made to keep the Website up and running smoothly. However, Zinpro takes no
        responsibility for, and will not be liable for, the Website being temporarily unavailable.`
            | translate
        }}
      </p>
      <p class="p">
        {{
          `Zinpro does not guarantee or warrant the accuracy, reliability, completeness or currency of
        the information contained in any report generated from this Website and underlying services,
        nor the usefulness in achieving any purpose. Report users are responsible for assessing the
        relevance and accuracy of the information in any report and hereby acknowledge that they will not rely on such report as an alternative to advice from a qualified professional.`
            | translate
        }}
      </p>
      <h2 class="h2">{{ 'General' | translate }}</h2>
      <p class="p">
        {{
          `We reserve the right to seek all remedies available at law and in equity for violations of
        these Terms of Use, including but not limited to the right to block access from a particular
        internet address to our Website and its features.` | translate
        }}
      </p>
      <p class="p">
        {{
          `Our failure to exercise or enforce any right or provisions of these Terms of Use shall not
        constitute a waiver of such right or provisions.` | translate
        }}
      </p>
      <p class="p">
        {{
          `If any provision of these Terms of Use is found to be invalid by any court having competent
        jurisdiction, the invalidity of such provision shall not affect the validity of the
        remaining provisions of these Terms of Use, which shall remain in full force and effect. No
        waiver of any of these Terms of Use shall be deemed a further or continuing waiver of such
        term or condition or any other term or condition. The provisions of these Terms of Use shall be binding on and inure to the benefit of both your successors and assignees and our successors and assignees.`
            | translate
        }}
      </p>
      <p class="p">
        {{
          `If you violate these Terms of Use in a way that causes harm to others, you agree to
        indemnify and hold Zinpro harmless against any liability for that harm.` | translate
        }}
      </p>
      <p class="p">
        {{
          `By using this Website, you agree that the only proper jurisdiction and venue for any dispute
        with Zinpro, or in any way relating to your use of this Website, is in the state and federal
        courts in the State of Minnesota, U.S.A. You further agree and consent to the exercise of
        personal jurisdiction in these courts in connection with any dispute involving Zinpro or its
        affiliates, employees, officers, directors, agents or suppliers.` | translate
        }}
      </p>
      <p class="p">
        {{
          `These Terms of Use are governed by the internal substantive laws of the State of Minnesota,
        without respect to its conflict of laws principles, and the federal laws of the United
        States.` | translate
        }}
      </p>
      <p class="p">
        {{
          `These Terms of Use constitute the entire agreement between you and Zinpro related to your
        use of the Website and its content.` | translate
        }}
      </p>
      <p class="font-xs">{{ 'Last updated: May 2025' | translate }}</p>
    </div>
  </div>
</template>
<script>
export default {
  metaInfo: {
    title: 'Generate Report',
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
  mounted() {
    this.scrollToTop();
  },
};
</script>
